<template>
  <DashboardDetailLayout
    title="Expenses"
    subtitle="Live data from the expense system."
    :sampleData="false"
  >
    <ExpenseMetricsRow />

    <ExpenseDailyChart
      :records="records"
      :transitions="transitions"
      :loading="loading"
      :errorMsg="auditErrorMsg"
    />

    <div class="rounded-md border-2 border-gray-300 bg-neutral-50 shadow p-3 m-2">
      <div class="flex flex-row items-center justify-between mb-2">
        <div class="text-sm tracking-widest text-gray-700 font-poppins uppercase">
          Recent Expenses
        </div>
        <div class="text-xs text-gray-500">
          <span v-if="loading">Loading…</span>
          <span v-else-if="errorMsg" class="text-rose-600">{{ errorMsg }}</span>
          <span v-else>Showing {{ visibleRecords.length }} of {{ records.length }} records</span>
        </div>
      </div>

      <div class="flex flex-row flex-wrap gap-1 mb-2">
        <button
          v-for="opt in statusFilters"
          :key="opt.value"
          @click="statusFilter = opt.value"
          :class="[
            'text-xs font-poppins px-2 py-0.5 rounded-full border',
            statusFilter === opt.value
              ? 'bg-dark-primary text-white border-dark-primary'
              : 'bg-white text-gray-600 border-gray-300 hover:border-accent-primary',
          ]"
        >
          {{ opt.label }}
        </button>
      </div>

      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500 border-b">
            <th class="py-1">Date</th>
            <th class="py-1">Submitter</th>
            <th class="py-1 text-right">Amount</th>
            <th class="py-1">Category</th>
            <th class="py-1">Status</th>
            <th class="py-1">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && visibleRecords.length === 0">
            <td colspan="6" class="py-4 text-center text-gray-500 italic">
              No expenses match this filter.
            </td>
          </tr>
          <tr
            v-for="row in visibleRecords"
            :key="row.id"
            class="border-b last:border-0"
          >
            <td class="py-1">{{ row.date_of_expense }}</td>
            <td class="py-1">{{ submitterName(row) }}</td>
            <td class="py-1 text-right font-mono">{{ formatDollars(row.amount) }}</td>
            <td class="py-1">{{ row.category ? row.category.name : '' }}</td>
            <td class="py-1">
              <span :class="['text-xs px-1.5 py-0.5 rounded-full', statusToneClass(row.status)]">
                {{ statusLabel(row.status) }}
              </span>
            </td>
            <td class="py-1 text-gray-700 truncate max-w-md">{{ row.description }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </DashboardDetailLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import DashboardDetailLayout from '../DashboardDetailLayout.vue'
import ExpenseMetricsRow from '../ExpenseMetricsRow.vue'
import ExpenseDailyChart from '../ExpenseDailyChart.vue'
import { getExpenses, getExpenseReport } from '@/services/Expense'

const store = useStore()

const loading = ref(true)
const errorMsg = ref('')
const auditErrorMsg = ref('')
const records = ref([])
const transitions = ref([])
const statusFilter = ref('all')

const statusFilters = [
  { value: 'all',                 label: 'All' },
  { value: 'draft',               label: 'Draft' },
  { value: 'submitted',           label: 'Awaiting Manager' },
  { value: 'manager_approved',    label: 'Awaiting Accounting' },
  { value: 'accounting_approved', label: 'Approved' },
  { value: 'closed',              label: 'Closed' },
  { value: 'rejected',            label: 'Rejected' },
]

const statusLabels = {
  draft: 'Draft',
  submitted: 'Awaiting Manager',
  manager_approved: 'Awaiting Accounting',
  accounting_approved: 'Approved',
  closed: 'Closed',
  rejected: 'Rejected',
}

function statusLabel(s) {
  return statusLabels[s] || s
}

function statusToneClass(s) {
  switch (s) {
    case 'draft':                return 'bg-gray-100 text-gray-700'
    case 'submitted':            return 'bg-amber-100 text-amber-800'
    case 'manager_approved':     return 'bg-sky-100 text-sky-800'
    case 'accounting_approved':  return 'bg-emerald-100 text-emerald-700'
    case 'closed':               return 'bg-emerald-50 text-emerald-700'
    case 'rejected':             return 'bg-rose-100 text-rose-700'
    default:                     return 'bg-gray-100 text-gray-700'
  }
}

function submitterName(row) {
  if (row.submitter && row.submitter.full_name) return row.submitter.full_name
  if (row.submitter && row.submitter.username) return row.submitter.username
  return ''
}

function formatDollars(n) {
  const v = Number(n) || 0
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const visibleRecords = computed(() => {
  const sorted = records.value
    .slice()
    .sort((a, b) => (b.date_of_expense || '').localeCompare(a.date_of_expense || ''))
  if (statusFilter.value === 'all') return sorted.slice(0, 100)
  return sorted.filter((r) => r.status === statusFilter.value).slice(0, 100)
})

async function load() {
  const token = store.state.auth.token
  if (!token) {
    errorMsg.value = 'Not authenticated.'
    loading.value = false
    return
  }
  try {
    const [recordsResult, auditResult] = await Promise.all([
      getExpenses(token, '?get_all=true'),
      getExpenseReport(token, 'audit', '?limit=2000').catch((e) => {
        const d = e && e.response && e.response.status ? `HTTP ${e.response.status}` : (e && e.message) || 'unknown error'
        auditErrorMsg.value = `audit: ${d}`
        return { transitions: [] }
      }),
    ])
    records.value = Array.isArray(recordsResult) ? recordsResult : (recordsResult.results || [])
    transitions.value = (auditResult && auditResult.transitions) || []
  } catch (e) {
    const detail = e && e.response && e.response.status ? `HTTP ${e.response.status}` : (e && e.message) || 'unknown error'
    errorMsg.value = detail
  } finally {
    loading.value = false
  }
}

load()
</script>
